import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Text as ChakraText,
  Circle,
  Divider,
  Flex,
  HStack,
  Stack,
  createIcon,
  useBreakpointValue,
} from '@chakra-ui/react';

import ChildLink from './child-link';
import ParentLink from './parent-link';
import React from 'react';
import { hexToRGBA } from '../utils/hexToRGBA';

export default function Layout(props) {
  const { useState, useEffect } = React;
  const { user, company, initialOpen, accessHelper, application, forceRender = false, translations, urlLookup } = props;
  const { isAllowed, allowGeneralActivity } = accessHelper;
  const [navOpen, setNavOpen] = useState(initialOpen);
  const isDesktop = useBreakpointValue({ base: false, lg: true });


  const allowance = {
    content: allowGeneralActivity('content', ['admin', 'creator', 'collaborator']),
    workshops: company.workshops && allowGeneralActivity('workshops', ['admin', 'creator', 'collaborator']),
    assessments: company.assessments && allowGeneralActivity('assessments', ['admin', 'creator', 'collaborator']),
    badges: company.feature_flags.enable_badges && isAllowed(['admin', 'badges'], null, ['admin']),
    surveys: company.feature_flags.enable_surveys && isAllowed(['admin', 'surveys'], null, ['admin']),
    assignmentsV2: company.feature_flags.enable_assignment_view_v2,
    assignments: allowGeneralActivity('assignments', ['admin', 'creator']),
    workflows: isAllowed(['admin', 'workflows'], null, ['admin']),
    journeys: company.feature_flags.journeys && allowGeneralActivity('journeys', ['admin', 'creator']),
    users: allowGeneralActivity('users', ['admin']),
    theme: isAllowed(['explore', 'update_theme'], null, ['admin']),
    settings: isAllowed(['admin', 'settings'], null, ['admin']),
    overview: isAllowed(['admin', 'overview'], null, ['admin']),
    reports: company.reports && isAllowed(['admin', 'reports'], null, ['admin']),
    continuInsights: company.feature_flags.enable_continu_insights && isAllowed(['admin', 'reports'], null, ['admin']),
  }

  const dedicatedWorkshopsEnabled = company.feature_flags.enable_dedicated_workshops && !user.is_purchaser
  
  const managerEnabled = user.is_manager || user.is_grader;
  
  const assignmentsEnabled = allowance.assignmentsV2
    ? allowance.assignments || allowance.workflows
    : allowance.assignments;

  const createEnabled =
    !allowance.inPartner &&
    (allowance.content ||
      allowance.workshops ||
      allowance.assessments ||
      allowance.badges ||
      allowance.surveys ||
      assignmentsEnabled ||
      allowance.workflows);

  const adminEnabled = allowance.users || allowance.theme || allowance.settings;


const reportingEnabled = allowance.overview || allowance.reports || allowance.continuInsights;

  // urlLookup.assessmentsOverview() returns either an absolute React path (flag on) or an
  // Angular hash route (flag off) — normalize the latter with the '/pvt/' prefix the rest of
  // this nav's Angular entries use.
  const assessmentsOverviewUrl = urlLookup.assessmentsOverview();
  const assessmentsIsAngular = assessmentsOverviewUrl.startsWith('#');
  const assessmentsRoute = assessmentsIsAngular
    ? `/pvt/${assessmentsOverviewUrl}`
    : assessmentsOverviewUrl;

const brandColor = application.theme.colors.brand.primary


  const Admin = createIcon({
      displayName: 'Admin',
      viewBox: '0 0 15 16',
      path: [
        <svg fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M7.27273 16C3.23127 15.1018 0 11.2887 0 7.27273V2.90909L7.27273 0L14.5455 2.90909V7.27273C14.5455 11.2902 11.3142 15.1018 7.27273 16ZM1.45455 3.63636V7.27273C1.49611 8.95428 2.08754 10.5759 3.13831 11.8894C4.18909 13.2028 5.64132 14.1358 7.27273 14.5455C8.90414 14.1358 10.3564 13.2028 11.4071 11.8894C12.4579 10.5759 13.0493 8.95428 13.0909 7.27273V3.63636L7.27273 1.45455L1.45455 3.63636Z"
            fill="currentColor"
          />
          <path
            d="M7.27277 7.27284C8.27693 7.27284 9.09095 6.45881 9.09095 5.45466C9.09095 4.4505 8.27693 3.63647 7.27277 3.63647C6.26862 3.63647 5.45459 4.4505 5.45459 5.45466C5.45459 6.45881 6.26862 7.27284 7.27277 7.27284Z"
            fill="currentColor"
          />
          <path
            d="M3.63623 10.1818C3.9947 10.8351 4.5203 11.3813 5.15924 11.7647C5.79818 12.148 6.52752 12.3548 7.27259 12.3636C8.01767 12.3548 8.747 12.148 9.38595 11.7647C10.0249 11.3813 10.5505 10.8351 10.909 10.1818C10.8908 8.80291 8.47841 8 7.27259 8C6.06023 8 3.65441 8.80291 3.63623 10.1818Z"
            fill="currentColor"
          />
        </svg>,
      ],
    });

  const BarChart = createIcon({
    displayName: 'BarChart',
    viewBox: '0 0 14 15',
    path: [
      <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M8.16667 1.76752H5.83333C5.51133 1.76752 5.25 2.02885 5.25 2.35085V12.8509C5.25 13.1728 5.51133 13.4342 5.83333 13.4342H8.16667C8.48867 13.4342 8.75 13.1728 8.75 12.8509V2.35085C8.75 2.02885 8.48867 1.76752 8.16667 1.76752Z"
          fill="currentColor"
        />
        <path
          d="M2.91667 8.1842H0.583333C0.261333 8.1842 0 8.44554 0 8.76754V12.8509C0 13.1729 0.261333 13.4342 0.583333 13.4342H2.91667C3.23867 13.4342 3.5 13.1729 3.5 12.8509V8.76754C3.5 8.44554 3.23867 8.1842 2.91667 8.1842Z"
          fill="currentColor"
        />
        <path
          d="M13.4167 5.26752H11.0833C10.7613 5.26752 10.5 5.52885 10.5 5.85085V12.8509C10.5 13.1729 10.7613 13.4342 11.0833 13.4342H13.4167C13.7387 13.4342 14 13.1729 14 12.8509V5.85085C14 5.52885 13.7387 5.26752 13.4167 5.26752Z"
          fill="currentColor"
        />
      </svg>,
    ],
  });

  const Create = createIcon({
    displayName: 'Create',
    viewBox: '0 0 16 16',
    d: 'M6.22222 8.88889H0.888889C0.653141 8.88889 0.427048 8.98254 0.260349 9.14924C0.0936505 9.31594 0 9.54203 0 9.77778V15.1111C0 15.3469 0.0936505 15.573 0.260349 15.7397C0.427048 15.9064 0.653141 16 0.888889 16H6.22222C6.45797 16 6.68406 15.9064 6.85076 15.7397C7.01746 15.573 7.11111 15.3469 7.11111 15.1111V9.77778C7.11111 9.54203 7.01746 9.31594 6.85076 9.14924C6.68406 8.98254 6.45797 8.88889 6.22222 8.88889ZM5.33333 14.2222H1.77778V10.6667H5.33333V14.2222ZM15.1111 0H9.77778C9.54203 0 9.31594 0.0936505 9.14924 0.260349C8.98254 0.427048 8.88889 0.653141 8.88889 0.888889V6.22222C8.88889 6.45797 8.98254 6.68406 9.14924 6.85076C9.31594 7.01746 9.54203 7.11111 9.77778 7.11111H15.1111C15.3469 7.11111 15.573 7.01746 15.7397 6.85076C15.9064 6.68406 16 6.45797 16 6.22222V0.888889C16 0.653141 15.9064 0.427048 15.7397 0.260349C15.573 0.0936505 15.3469 0 15.1111 0ZM14.2222 5.33333H10.6667V1.77778H14.2222V5.33333ZM15.1111 11.5556H13.3333V9.77778C13.3333 9.54203 13.2397 9.31594 13.073 9.14924C12.9063 8.98254 12.6802 8.88889 12.4444 8.88889C12.2087 8.88889 11.9826 8.98254 11.8159 9.14924C11.6492 9.31594 11.5556 9.54203 11.5556 9.77778V11.5556H9.77778C9.54203 11.5556 9.31594 11.6492 9.14924 11.8159C8.98254 11.9826 8.88889 12.2087 8.88889 12.4444C8.88889 12.6802 8.98254 12.9063 9.14924 13.073C9.31594 13.2397 9.54203 13.3333 9.77778 13.3333H11.5556V15.1111C11.5556 15.3469 11.6492 15.573 11.8159 15.7397C11.9826 15.9064 12.2087 16 12.4444 16C12.6802 16 12.9063 15.9064 13.073 15.7397C13.2397 15.573 13.3333 15.3469 13.3333 15.1111V13.3333H15.1111C15.3469 13.3333 15.573 13.2397 15.7397 13.073C15.9064 12.9063 16 12.6802 16 12.4444C16 12.2087 15.9064 11.9826 15.7397 11.8159C15.573 11.6492 15.3469 11.5556 15.1111 11.5556ZM6.22222 0H0.888889C0.653141 0 0.427048 0.0936505 0.260349 0.260349C0.0936505 0.427048 0 0.653141 0 0.888889V6.22222C0 6.45797 0.0936505 6.68406 0.260349 6.85076C0.427048 7.01746 0.653141 7.11111 0.888889 7.11111H6.22222C6.45797 7.11111 6.68406 7.01746 6.85076 6.85076C7.01746 6.68406 7.11111 6.45797 7.11111 6.22222V0.888889C7.11111 0.653141 7.01746 0.427048 6.85076 0.260349C6.68406 0.0936505 6.45797 0 6.22222 0ZM5.33333 5.33333H1.77778V1.77778H5.33333V5.33333Z',
  });

  const Dashboard = createIcon({
    displayName: 'Dashboard',
    viewBox: '0 0 16 16',
    d: 'M13.6638 2.36332C13.6582 2.35692 13.6558 2.34813 13.6502 2.34253C13.6446 2.33693 13.6358 2.33454 13.6294 2.32814C12.1337 0.837202 10.108 0 7.99614 0C5.88428 0 3.85854 0.837202 2.36286 2.32814C2.35646 2.33454 2.34847 2.33613 2.34207 2.34253C2.33567 2.34893 2.33407 2.35692 2.32848 2.36332C1.21403 3.48312 0.456282 4.90796 0.150855 6.45801C-0.154573 8.00805 0.00601358 9.61384 0.612346 11.0727C1.21868 12.5316 2.24358 13.7781 3.55771 14.6551C4.87183 15.532 6.41628 16 7.99614 16C9.57599 16 11.1204 15.532 12.4346 14.6551C13.7487 13.7781 14.7736 12.5316 15.3799 11.0727C15.9863 9.61384 16.1468 8.00805 15.8414 6.45801C15.536 4.90796 14.7782 3.48312 13.6638 2.36332ZM7.99614 14.3935C7.03841 14.3932 6.09304 14.1774 5.23002 13.7621C4.367 13.3469 3.60845 12.7428 3.01055 11.9947H6.22099C6.44339 12.2461 6.71664 12.4473 7.02266 12.5852C7.32868 12.7231 7.66049 12.7944 7.99614 12.7944C8.33178 12.7944 8.66359 12.7231 8.96961 12.5852C9.27564 12.4473 9.54889 12.2461 9.77128 11.9947H12.9817C12.3838 12.7428 11.6253 13.3469 10.7623 13.7621C9.89924 14.1774 8.95386 14.3932 7.99614 14.3935ZM7.19652 10.3954C7.19652 10.2373 7.24342 10.0827 7.33128 9.95119C7.41914 9.8197 7.54403 9.71721 7.69014 9.65669C7.83625 9.59617 7.99702 9.58033 8.15213 9.61119C8.30724 9.64204 8.44972 9.7182 8.56155 9.83002C8.67338 9.94185 8.74953 10.0843 8.78038 10.2394C8.81124 10.3945 8.7954 10.5553 8.73488 10.7014C8.67436 10.8475 8.57187 10.9724 8.44038 11.0603C8.30888 11.1482 8.15428 11.195 7.99614 11.195C7.78413 11.1948 7.58087 11.1105 7.43096 10.9606C7.28105 10.8107 7.19673 10.6074 7.19652 10.3954ZM13.9213 10.397L13.9133 10.3954H10.395C10.3935 9.90093 10.239 9.419 9.95265 9.01583C9.6663 8.61265 9.26217 8.30801 8.79575 8.14373V5.59776C8.79575 5.38569 8.7115 5.1823 8.56155 5.03235C8.41159 4.88239 8.20821 4.79814 7.99614 4.79814C7.78406 4.79814 7.58068 4.88239 7.43072 5.03235C7.28077 5.1823 7.19652 5.38569 7.19652 5.59776V8.14373C6.7301 8.30801 6.32597 8.61265 6.03962 9.01583C5.75328 9.419 5.59876 9.90093 5.5973 10.3954H2.079L2.071 10.397C1.86311 9.88387 1.72332 9.34568 1.6552 8.79621H2.39884C2.61091 8.79621 2.8143 8.71197 2.96425 8.56201C3.11421 8.41205 3.19846 8.20867 3.19846 7.9966C3.19846 7.78453 3.11421 7.58114 2.96425 7.43118C2.8143 7.28123 2.61091 7.19698 2.39884 7.19698H1.6552C1.79804 6.06036 2.24533 4.98345 2.94978 4.08009L3.47272 4.60384C3.54649 4.68021 3.63472 4.74113 3.73227 4.78303C3.82983 4.82494 3.93476 4.847 4.04093 4.84792C4.1471 4.84884 4.25239 4.82861 4.35066 4.78841C4.44893 4.7482 4.53821 4.68883 4.61329 4.61375C4.68837 4.53867 4.74774 4.44939 4.78794 4.35112C4.82815 4.25285 4.84838 4.14756 4.84746 4.04139C4.84654 3.93522 4.82448 3.83029 4.78257 3.73274C4.74066 3.63518 4.67975 3.54695 4.60338 3.47318L4.08043 2.94944C4.98367 2.24544 6.06027 1.79844 7.19652 1.65566V2.3993C7.19652 2.61137 7.28077 2.81476 7.43072 2.96472C7.58068 3.11467 7.78406 3.19892 7.99614 3.19892C8.20821 3.19892 8.41159 3.11467 8.56155 2.96472C8.7115 2.81476 8.79575 2.61137 8.79575 2.3993V1.65566C9.932 1.79844 11.0086 2.24544 11.9118 2.94944L11.3889 3.47318C11.3125 3.54695 11.2516 3.63518 11.2097 3.73274C11.1678 3.83029 11.1457 3.93522 11.1448 4.04139C11.1439 4.14756 11.1641 4.25285 11.2043 4.35112C11.2445 4.44939 11.3039 4.53867 11.379 4.61375C11.4541 4.68883 11.5433 4.7482 11.6416 4.78841C11.7399 4.82861 11.8452 4.84884 11.9513 4.84792C12.0575 4.847 12.1624 4.82494 12.26 4.78303C12.3576 4.74113 12.4458 4.68021 12.5195 4.60384L13.0425 4.08009C13.7472 4.98336 14.1948 6.06028 14.3379 7.19698H13.5934C13.3814 7.19698 13.178 7.28123 13.028 7.43118C12.8781 7.58114 12.7938 7.78453 12.7938 7.9966C12.7938 8.20867 12.8781 8.41205 13.028 8.56201C13.178 8.71197 13.3814 8.79621 13.5934 8.79621H14.3371C14.269 9.34568 14.1292 9.88387 13.9213 10.397Z',
  });

  const Explore = createIcon({
    displayName: 'Explore',
    viewBox: '0 0 16 16',
    d: 'M8.88889 5.33333V0H16V5.33333H8.88889ZM0 8.88889V0H7.11111V8.88889H0ZM8.88889 16V7.11111H16V16H8.88889ZM0 16V10.6667H7.11111V16H0Z',
  });

  const Manager = createIcon({
    displayName: 'Manager',
    viewBox: '0 0 16 16',
    path: [
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0 8C0 12.4112 3.58885 16 8 16C12.4112 16 16 12.4112 16 8C16 3.58885 12.4112 0 8 0C3.58885 0 0 3.58885 0 8ZM1.23077 8C1.23077 4.26923 4.26923 1.23077 8 1.23077C11.7327 1.23077 14.7692 4.26731 14.7692 8C14.7692 9.20456 14.4528 10.3366 13.8988 11.3175C13.8718 11.299 13.8434 11.2806 13.8135 11.2623C12.9112 10.7112 11.8012 10.4196 10.6027 10.4196C9.42698 10.4196 8.28621 10.7235 7.39006 11.2754C6.38044 11.8973 5.70198 12.8042 5.42852 13.8977C5.40581 13.9881 5.37945 14.1059 5.35918 14.2323C5.02938 14.0919 4.71289 13.9261 4.4122 13.7373C4.42299 13.6884 4.43471 13.6397 4.44736 13.5912C4.71929 12.5062 5.29813 11.6016 6.19621 10.87C6.23867 10.835 6.27095 10.7892 6.2897 10.7375C6.30844 10.6857 6.31297 10.6299 6.30279 10.5758C6.29262 10.5217 6.26812 10.4713 6.23185 10.4299C6.19559 10.3885 6.14888 10.3576 6.0966 10.3404C5.63506 10.1931 5.10813 10.1142 4.45467 10.1142C3.54178 10.1142 2.57275 10.3606 1.84172 10.8083C1.44953 9.95225 1.23077 9.00105 1.23077 8ZM8.78198 8.54886C9.28698 9.08771 9.93352 9.38463 10.6027 9.38463C11.2716 9.38463 11.9177 9.08771 12.4231 8.54886C12.9218 8.00685 13.2204 7.3107 13.2693 6.57578C13.3258 5.78694 13.0754 5.05001 12.577 4.51771C12.0785 3.9854 11.3774 3.69232 10.6027 3.69232C9.82236 3.69232 9.12352 3.97924 8.62352 4.51271C8.12352 5.04617 7.87929 5.77886 7.93583 6.57501C7.98382 7.31038 8.28247 8.00706 8.78198 8.54886ZM2.21121 7.15501C2.30352 8.45578 3.31006 9.51386 4.45467 9.51386C5.59736 9.51386 6.60352 8.45617 6.69737 7.15732C6.74544 6.49655 6.53198 5.87655 6.11083 5.42655C5.68967 4.97655 5.10544 4.72886 4.45429 4.72886C3.80313 4.72886 3.21852 4.96924 2.79467 5.42117C2.37083 5.87309 2.1639 6.48886 2.21121 7.15501Z"
        fill="currentColor"
      />,
    ],
  });

  const  Workshop = createIcon({
    displayName: 'Workshop',
    viewBox: '0 0 27 23',
    path: [
      <svg fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M2.48476 7.04858C2.48476 7.04858 8.37812 22.7737 15.4767 21.2885C20.9003 20.1544 23.8755 10.5979 24.9763 6.15651C25.1519 5.45425 24.312 4.94653 23.7711 5.43052L17.5598 10.9965C16.3593 12.0736 14.5372 12.0499 13.3652 10.9443L4.3211 2.4269C3.92251 2.05205 3.49071 1.71515 3.01621 1.44468C1.6354 0.642765 -0.447678 0.163515 2.48476 7.04858Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeMiterlimit="10"
        />
        <path
          d="M13.4696 21.2932C14.0484 21.4118 14.5799 21.4166 15.0117 21.3644C19.1304 20.8756 14.8361 8.47209 13.721 2.48383C13.5645 1.6487 12.3972 1.58702 12.122 2.40317L8.13612 14.1756C7.65212 15.5992 7.98902 17.1745 9.01869 18.2659C10.1955 19.5138 11.8562 20.9611 13.4696 21.2932Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeMiterlimit="10"
        />
      </svg>,
    ],
  });


  const navLinks = [
      {
        name: translations?.explore || 'Explore',
        icon: <Explore />,
        route: '/explore',
        enabled: true,
        children: null,
        isAngular: false,
      },
      {
        name: translations?.dashboard || 'Dashboard',
        icon: <Dashboard />,
        route: '/dashboard',
        enabled: true,
        children: null,
        isAngular: false,
      },
      {
        name: translations?.workshops || 'Workshops',
        icon: <Workshop />,
        enabled: dedicatedWorkshopsEnabled,
        route: '/workshops',
        children: null,
        isAngular: false,
      },
      {
        divider: true,
        enabled: true,
      },
      {
        name: translations?.managers || 'Managers',
        icon: <Manager />,
        route: '/pvt/#/manager',
        enabled: managerEnabled,
        children: null,
        isAngular: true,
      },
      {
        name: translations?.create || 'Create',
        icon: <Create />,
        enabled: createEnabled,
        children: [
          {
            name: translations?.createContent || 'Content',
            route: '/admin/content',
            enabled: allowance.content,
            isAngular: false,
          },
          {
            // Flag on, this entry and Content land on the same page (/admin/content) —
            // hide it unless it's the user's only path there. Removed entirely at flag cleanup.
            name: translations?.createAssessments || 'Assessments',
            route: assessmentsRoute,
            enabled: allowance.assessments && (assessmentsIsAngular || !allowance.content),
            isAngular: assessmentsIsAngular,
          },
          {
            name: translations?.createWorkshops || 'Workshops',
            route: '/pvt/#/overview/workshops',
            enabled: allowance.workshops,
            isAngular: true,
          },
          {
            name: translations?.createBadges || 'Badges',
            route: '/admin/badges',
            enabled: allowance.badges,
            isAngular: false,
          },
          {
            name: translations?.createSurveys || 'Surveys',
            route: '/admin/surveys',
            enabled: allowance.surveys,
            isAngular: false,
          },
          {
            name: translations?.createAssignments || 'Assignments',
            route: allowance.assignmentsV2
              ? '/admin/assignments/overview?type=all'
              : '/pvt/#/overview/assignments',
            enabled: allowance.assignmentsV2
              ? allowance.assignments || allowance.workflows
              : allowance.assignments,
            isAngular: !allowance.assignmentsV2,
          },
          {
            name: translations?.createWorkflows || 'Workflows',
            route: '/pvt/#/overview/workflows',
            enabled: allowance.workflows && !allowance.assignmentsV2,
            isAngular: true,
          },
        ],
      },
      {
        name: translations?.admin || 'Admin',
        icon: <Admin />,
        enabled: adminEnabled,
        children: [
          {
            name: translations?.adminUsers || 'Users',
            route: '/admin/users',
            enabled: allowance.users,
            isAngular: false,
          },
          {
            name: translations?.adminTheme || 'Theme',
            route: '/admin/theme',
            enabled: allowance.theme,
            isAngular: true,
          },
          {
            name: translations?.adminIntegrations || 'Integrations',
            route: '/pvt/#/overview/settings/integrations',
            enabled: allowance.settings,
            isAngular: true,
          },
          {
            name: translations?.adminUtilities || 'Admin Utilities',
            route: '/admin/utilities',
            enabled: true,
            isAngular: false,
          },
        ],
      },
      {
        name: translations?.reporting || 'Reporting',
        icon: <BarChart />,
        enabled: reportingEnabled,
        isAngular: false,
        children: [
          {
            name: translations?.reportingOverview || 'Overview',
            route: '/pvt/#/overview/admin',
            enabled: allowance.overview,
            isAngular: true,
          },
          {
            name: translations?.reportingReports || 'Reports',
            route: '/admin/reports',
            enabled: allowance.reports,
            isAngular: false,
          },
          {
            name: translations?.reportingInsights || 'Insights',
            route: '/admin/insights',
            enabled: allowance.continuInsights,
            isAngular: false,
          },
        ],
      },
      // {
      //   name: 'Partner Manager',
      //   icon: <Manager />,
      //   route: '/extend/manager',
      //   enabled: allowance.inPartner && isPartnerPrincipal(),
      //   isAngular: false,
      //   children: null,
      // },
  ];

  const parentLinkStyles = {
    alignItems: 'center',
    width: 'full',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#3a3a3a',
    paddingY: 3,
    paddingX: 4,
    _hover: {
      cursor: 'pointer',
      backgroundColor: hexToRGBA(brandColor, 0.1),
      color: '#3a3a3a',
      fontWeight: 600,
    },
  };

  return (forceRender || isDesktop) && (
    <Box
      position="fixed"
      top={{ base: '30px', md: '80px' }}
      bottom={0}
      zIndex={1000}
      width={initialOpen ? '275px' : '80px'}
      backgroundColor="white"  
      transition=".2s ease all"
      transform="translate(0)"
      overflowY="auto"
      paddingTop={3}
      paddingX={2}
      boxShadow={{ base: 'none', lg: 'lg' }}
      _hover={{ base: {}, lg: { width: '224px' } }}
      onMouseEnter={isDesktop ? () => setNavOpen(true) : undefined}
      onMouseLeave={
        isDesktop
          ? () => {
              if (!initialOpen) {
                setNavOpen(false);
              }
            }
          : undefined
      }
      
    >
       <Stack overflow="hidden" spacing={0}>
        <Accordion
          index={!navOpen ? 99 : undefined} // ! Handles closing the accordion when the nav is closed
          allowToggle
          width="full"
        >
          {navLinks.map((link) => {
            if (!link.enabled) return null;

            if (link.divider)
              return (
                <Divider
                  key={'divider_' + (link.name || 'default')}
                  width="full"
                  marginY={2}
                  borderWidth={1}
                  borderColor="#ededed"
                />
              );

            return link.children ? (
              <AccordionItem border="none" width="full">
                {({ isExpanded }) => (
                  <>
                    <h6>
                      <AccordionButton sx={
                          isExpanded
                            ? {
                                ...parentLinkStyles,
                                backgroundColor: hexToRGBA(brandColor, 0.1),
                                color: 'neutral.1500',
                                fontWeight: 600,
                              }
                            : parentLinkStyles
                        }>
                        <Flex
                          flex="1"
                          width="full"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <HStack spacing={2}>
                            <Circle
                              backgroundColor={hexToRGBA(brandColor, 0.1)}
                              color="#3a3a3a"
                              size="32px"
                            >
                              {link.icon}
                            </Circle>

                            {navOpen && <ChakraText>{link.name}</ChakraText>}
                          </HStack>

                          {navOpen && <AccordionIcon boxSize={6} color="#3a3a3a" />}
                        </Flex>
                      </AccordionButton>
                    </h6>

                    <AccordionPanel marginTop={0} paddingX={0}>
                      <Stack
                        height={(link.children?.filter((child) => child.enabled).length || 0) * 35}
                        spacing={0}
                      >
                        {link.children?.map((child) => {
                          if (!child.enabled) return null;

                          return (
                            
                            <ChildLink
                              key={'childlink_' + (child.name || 'default')}
                              navLink={child}
                              application={application}
                            />
                          );
                        })}
                      </Stack>
                    </AccordionPanel>
                  </>
                )}
              </AccordionItem>
            ) : (
              <ParentLink navLink={link} navOpen={navOpen} company={company} application={application} />
            );
          })}
        </Accordion>
      </Stack> 
    </Box>
  );
}
